import React from 'react';
import { View } from 'react-native';
import { router, usePathname, useSegments } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';
import { useAppSelector } from '@/store/hooks';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/ui/Button';
import { SignInPanel } from '@/components/auth/SignInPanel';
import { IconButton } from '@/components/ui/IconButton';
import { setPostLoginRoute } from '@/utils/postLoginRoute';

/**
 * Top-level routes that only make sense for a signed-in customer. Everything not listed here
 * (home, discover, shop and stylist pages, search, settings, legal pages) stays open to guests.
 * The booking flow guards itself (booking/_layout.tsx).
 */
const ACCOUNT_ONLY_ROUTES = new Set([
  'edit-profile',
  'change-password',
  'security-settings',
  'notification-settings',
  'notifications',
  'notification',
  'loyalty',
  'my-reviews',
  'review',
  'appointment',
  'referrals',
  'support',
  'messages',
]);

/** True when the current route needs an account and nobody is signed in (after start-up finishes). */
export function useNeedsSignIn(): boolean {
  const segments = useSegments();
  const isBootstrapping = useAppSelector((state) => state.auth.isBootstrapping);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const first = String(segments[0] ?? '');
  return !isBootstrapping && !isAuthenticated && ACCOUNT_ONLY_ROUTES.has(first);
}

/** Full-screen sign-in prompt shown in place of an account-only screen. */
export function SignInRequired() {
  const { colors } = useAppTheme();
  const { tText } = useTranslation();
  const insets = useSafeAreaInsets();
  const pathname = usePathname();

  const leave = () => {
    setPostLoginRoute(null);
    if (router.canGoBack()) router.back();
    else router.replace('/(tabs)');
  };

  return (
    <View style={[styles.root, { backgroundColor: colors.paper, paddingTop: insets.top + spacing.md }]}>
      <IconButton icon="arrow-back" accessibilityLabel={tText('Go back', 'পেছনে যান')} onPress={leave} variant="outlined" />
      <View style={styles.body}>
        <SignInPanel
          returnTo={pathname}
          icon="lock-closed-outline"
          title={tText('Sign in to continue', 'চালিয়ে যেতে সাইন ইন করুন')}
          message={tText(
            'This part of SalunBD belongs to your account. Sign in or create one, it only takes a minute.',
            'SalunBD-এর এই অংশটি আপনার অ্যাকাউন্টের। সাইন ইন করুন বা নতুন অ্যাকাউন্ট খুলুন, এক মিনিটের বেশি লাগবে না।',
          )}
          footer={<Button label={tText('Back to browsing', 'ব্রাউজিংয়ে ফিরুন')} variant="ghost" onPress={leave} />}
        />
      </View>
    </View>
  );
}

/** Wrap the navigator: account-only screens are never mounted for a guest, the prompt shows instead. */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const blocked = useNeedsSignIn();
  return (
    <>
      {children}
      {blocked ? (
        <View style={styles.overlay}>
          <SignInRequired />
        </View>
      ) : null}
    </>
  );
}

const styles = ScaledSheet.create({
  overlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50 },
  root: { flex: 1, paddingHorizontal: spacing.lg },
  body: { flex: 1, justifyContent: 'center', paddingBottom: 60 },
});
